import httpService from './httpService';

export interface DownloadPageAnalytics {
  sessionId: string;
  variant: string;
  source: string;
  campaign: string;
  platform: string;
  timestamp: string;
  userAgent: string;
  referrer: string;
  url: string;
  event?: string;
}

export interface AnalyticsEvent {
  event: string;
  data: Record<string, any>;
  timestamp: string;
  sessionId: string;
}

class AnalyticsService {
  private sessionId: string | null = null;

  private analyticsQueue: DownloadPageAnalytics[] = [];

  private flushInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeSession();
    this.startQueueFlush();
  }

  private initializeSession() {
    let sessionId = sessionStorage.getItem('analyticsSessionId');
    if (!sessionId) {
      sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analyticsSessionId', sessionId);
    }
    this.sessionId = sessionId;
  }

  private startQueueFlush() {
    // Flush analytics queue every 10 seconds
    this.flushInterval = setInterval(() => {
      this.flushQueue();
    }, 10000);

    // Also flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flushQueue();
    });
  }

  private async flushQueue() {
    if (this.analyticsQueue.length === 0) return;

    const eventsToSend = [...this.analyticsQueue];
    this.analyticsQueue = [];

    try {
      // Try to send to backend
      await AnalyticsService.sendAnalytics(eventsToSend);
    } catch (error) {
      console.error('Failed to send analytics:', error);
      // Store failed events in localStorage as backup
      AnalyticsService.storeInLocalStorage(eventsToSend);
    }
  }

  private static async sendAnalytics(events: DownloadPageAnalytics[]) {
    try {
      // TODO: Replace with actual analytics endpoint
      await httpService.post('/landing-analytics/download-page', {
        events,
      });
    } catch (error) {
      // For now, just store in localStorage
      AnalyticsService.storeInLocalStorage(events);
      throw error;
    }
  }

  private static storeInLocalStorage(events: DownloadPageAnalytics[]) {
    try {
      const existingData = JSON.parse(localStorage.getItem('downloadPageAnalytics') || '[]');
      const updatedData = [...existingData, ...events];

      // Keep only last 1000 events to prevent localStorage from growing too large
      const trimmedData = updatedData.slice(-1000);

      localStorage.setItem('downloadPageAnalytics', JSON.stringify(trimmedData));
    } catch (error) {
      console.error('Failed to store analytics in localStorage:', error);
    }
  }

  public trackPageView(data: Omit<DownloadPageAnalytics, 'sessionId'>) {
    const analyticsData: DownloadPageAnalytics = {
      ...data,
      sessionId: this.sessionId!,
    };

    this.analyticsQueue.push(analyticsData);

    // Also store immediately in localStorage
    AnalyticsService.storeInLocalStorage([analyticsData]);
  }

  public trackEvent(event: string, data: Record<string, any>) {
    const analyticsEvent: DownloadPageAnalytics = {
      sessionId: this.sessionId!,
      event,
      ...data,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      url: window.location.href,
      variant: data.variant || '',
      source: data.source || '',
      campaign: data.campaign || '',
      platform: data.platform || '',
    };

    this.analyticsQueue.push(analyticsEvent);

    // Also store immediately in localStorage
    AnalyticsService.storeInLocalStorage([analyticsEvent]);
  }

  // Removed dashboard retrieval and clearing helpers as the dashboard page was removed

  public destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flushQueue();
  }
}

// Export singleton instance
const analyticsService = new AnalyticsService();

// Export static methods
// (none)

export default analyticsService;
